import React, { useMemo } from 'react'
import { BoxProps } from 'rebass'

import {
    MyRecentPaymentsQuery,
    PaymentStatus,
    useMyRecentPaymentsQuery,
    useMyUpcomingPaymentQuery,
} from '../../types/graphql'
import SFlex from 'components/opinionless/SFlex'
import SIcon from 'components/opinionless/SIcon'
import ReceiptSvg from '@svgs/receipt.svg'
import SText from 'components/opinionless/SText'
import { Trans, useTranslation } from '../../i18n'
import moment from 'moment-timezone'

import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'

import SBox from 'components/opinionless/SBox'
import { titleForTransferMethodType } from '../../utils/transferMethod'
import { formatMoney } from '../../utils/money'
import { Table, TableHeaderText } from '../../styles/table'
import { useSTheme } from 'styles/theme'
import ConditionalRender from '../../utils/conditionalRender'

type Props = BoxProps

const HomeRecentPayments: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const { data, loading } = useMyRecentPaymentsQuery()
    const { data: upcomingPaymentsData } = useMyUpcomingPaymentQuery()

    const upcomingPaymentDateStr = useMemo(() => {
        if (upcomingPaymentsData && upcomingPaymentsData.myUpcomingPayment) {
            const scheduledDate = moment(upcomingPaymentsData.myUpcomingPayment.scheduledDate).tz(
                window.ORGANIZATION_CONFIGS.timezone
            )
            return scheduledDate.format('MMMM D, YYYY')
        } else {
            return null
        }
    }, [upcomingPaymentsData])

    const renderMobileTable = () => {
        const renderTableCell = (payment: MyRecentPaymentsQuery['myRecentPayments'][0], idx: number) => {
            const isLast = idx === data.myRecentPayments.length - 1
            return (
                <SFlex
                    sx={{
                        py: 4,
                        borderBottomStyle: 'solid',
                        borderBottomWidth: 0.5,
                        borderBottomColor: isLast ? 'transparent' : 'gray150',
                    }}
                    key={payment.id}
                    flexDirection="row"
                    justifyContent="space-between"
                >
                    <SFlex flexDirection="column" alignItems="flex-start" flex="1 1 auto">
                        <SText fontSize={3} color="gray400">
                            {moment(payment.createdAt).tz(window.ORGANIZATION_CONFIGS.timezone).format('MMMM D, YYYY')}
                        </SText>
                        <SText fontSize={2} color="gray350">
                            <ConditionalRender if={payment.status === PaymentStatus.InProgress}>
                                {t('home_recent_payments_in_progress_via', {
                                    transferType: titleForTransferMethodType(payment.destination.type, t),
                                })}
                            </ConditionalRender>
                            <ConditionalRender if={payment.status === PaymentStatus.Completed}>
                                {t('home_recent_payments_delivered_via', {
                                    transferType: titleForTransferMethodType(payment.destination.type, t),
                                })}
                            </ConditionalRender>
                            <ConditionalRender
                                if={![PaymentStatus.InProgress, PaymentStatus.Completed].includes(payment.status)}
                            >
                                {t('home_recent_payments_failed_via', {
                                    transferType: titleForTransferMethodType(payment.destination.type, t),
                                })}
                            </ConditionalRender>
                        </SText>
                        <SText fontSize={2} color="gray350">
                            {payment.destination.halfLiner}
                        </SText>
                    </SFlex>
                    <SFlex flexDirection="column" alignItems="flex-end" justifyContent="center" flex="0 0 auto" ml={6}>
                        <SText fontWeight="bold" color="secondaryColor">
                            {formatMoney(payment.amount)}
                        </SText>
                    </SFlex>
                </SFlex>
            )
        }

        return (
            <SBox
                sx={{
                    alignItems: 'stretch',
                    flexDirection: 'column',
                    display: ['flex', 'none', 'none'],
                    width: '100%',
                    my: -4,
                }}
            >
                {data.myRecentPayments.map(renderTableCell)}
            </SBox>
        )
    }

    const renderNonMobileTable = () => {
        return (
            <Table
                as="table"
                sx={{
                    display: ['none', 'table', 'table'],
                }}
            >
                <thead>
                    <tr>
                        <th>
                            <TableHeaderText>{t('home_table_header_amount')}</TableHeaderText>
                        </th>
                        <th>
                            <TableHeaderText>{t('home_table_header_status')}</TableHeaderText>
                        </th>
                        <th>
                            <TableHeaderText>{t('home_table_header_date')}</TableHeaderText>
                        </th>
                        <th>
                            <TableHeaderText>{t('home_table_header_payment_method')}</TableHeaderText>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.myRecentPayments.map((payment: MyRecentPaymentsQuery['myRecentPayments'][0]) => {
                        return (
                            <SBox
                                as="tr"
                                sx={{
                                    py: 4,
                                }}
                                key={payment.id}
                            >
                                <td>
                                    <SText>{formatMoney(payment.amount)}</SText>
                                </td>
                                <td>
                                    <ConditionalRender if={payment.status === PaymentStatus.Completed}>
                                        <SText>{t('home_recent_payments_delivered')}</SText>
                                    </ConditionalRender>
                                    <ConditionalRender if={payment.status === PaymentStatus.InProgress}>
                                        <SText>{t('home_recent_payments_in_progress')}</SText>
                                    </ConditionalRender>
                                    <ConditionalRender
                                        if={
                                            ![PaymentStatus.InProgress, PaymentStatus.Completed].includes(
                                                payment.status
                                            )
                                        }
                                    >
                                        <SText>{t('home_recent_payments_failed')}</SText>
                                    </ConditionalRender>
                                </td>
                                <td>
                                    <SText>
                                        {moment(payment.createdAt)
                                            .tz(window.ORGANIZATION_CONFIGS.timezone)
                                            .format('MMMM D, YYYY')}
                                    </SText>
                                </td>
                                <td>
                                    <SText>{payment.destination.oneLiner}</SText>
                                </td>
                            </SBox>
                        )
                    })}
                </tbody>
            </Table>
        )
    }

    const renderMobileEmptyState = () => {
        return (
            <SText
                textAlign="left"
                mb={[3, 0, 0]}
                as="span"
                display={['block', 'none', 'none']}
                fontSize={4}
                color={'gray400'}
                lineHeight={1.4}
            >
                <Trans i18nKey="common:home_recent_payments_empty_mobile_text">
                    You haven’t received any payments yet. Your first payment will be delivered on{' '}
                    <SText
                        as="span"
                        sx={{
                            fontWeight: 'bold',
                        }}
                    >
                        {{ upcomingPaymentDateStr }}
                    </SText>
                    .
                </Trans>
            </SText>
        )
    }

    const renderNonMobileEmptyState = () => {
        return (
            <SBox
                sx={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: ['none', 'flex', 'flex'],
                }}
            >
                <SText
                    fontWeight="semiBold"
                    fontSize={18}
                    color="gray300"
                    textAlign="center"
                    lineHeight={1.2}
                    mb={[3, 0, 0]}
                    as="span"
                    fontFamily={theme.fonts.default}
                >
                    {t('home_recent_payments_empty_title')}
                </SText>
                <SText fontSize={2} color="gray300" mt={1} textAlign="center" as="span">
                    {t('home_recent_payments_empty_subtitle', { upcomingPaymentDateStr })}
                </SText>
            </SBox>
        )
    }

    const renderTableContent = () => {
        if (loading) {
            return <SLoadingIndicator color="backgroundColor" size={20} />
        } else if (data.myRecentPayments.length === 0) {
            return (
                <>
                    {renderMobileEmptyState()}
                    {renderNonMobileEmptyState()}
                </>
            )
        } else {
            return (
                <>
                    {renderMobileTable()}
                    {renderNonMobileTable()}
                </>
            )
        }
    }

    return (
        <SFlex
            sx={{
                borderRadius: [0, 2, 2],
                boxShadow: 'main',
                bg: 'white',
                flexDirection: 'column',
                p: [0, 8, 8],
                minHeight: [100, 400, 400],
            }}
        >
            <SFlex
                flexDirection="row"
                alignItems="center"
                sx={{
                    px: [6, 0, 0],
                    py: [5, 0, 0],
                    mb: [0, 6, 6],
                    borderBottomStyle: 'solid',
                    borderBottomWidth: [0.2, 0, 0],
                    borderBottomColor: 'gray150',
                }}
            >
                <SIcon display={['none', 'block', 'block']} icon={ReceiptSvg} size={20} tintColor="black" mr={4} />
                <SText
                    sx={{
                        fontWeight: 'bold',
                        fontSize: 4,
                        lineHeight: 1.4,
                        color: 'black',
                        mt: [2, 0, 0],
                    }}
                >
                    {t('home_recent_payments')}
                </SText>
            </SFlex>
            <SFlex
                flex="1 1 auto"
                justifyContent="center"
                sx={{
                    px: [6, 0, 0],
                    py: [6, 0, 0],
                }}
            >
                {renderTableContent()}
            </SFlex>
        </SFlex>
    )
}

export default HomeRecentPayments
